import { useCallback, useState } from 'react'
import type { PropsWithChildren } from 'react'
import { useIntl } from 'react-intl'
import { useLocation } from 'react-router-dom'
import loadable from '@loadable/component'
import { rem } from 'polished'
import styled from 'styled-components'
import { SPOOF_TOOLBAR_HEIGHT_CSS_VARIABLE } from 'constants/spoofing'
import IconButton from 'LuxKit/Button/IconButton'
import LineAngleRightIcon from 'LuxKit/Icons/line/LineAngleRightIcon'
import { useAppSelector } from 'hooks/reduxHooks'
import { useScreenSizeUp } from 'hooks/useScreenSize'
import CSSBreakpoint from 'components/utils/CSSBreakpoint'
import { mediaQueryUp } from 'components/utils/breakpoint'
import ErrorBoundary from 'components/Common/ErrorBoundary'
import useSalesSidebar from 'components/App/SalesCopilot/useSalesSidebar'
import useSalesSidebarEnabled from 'components/App/SalesCopilot/useSalesSidebarEnabled'
import zIndex from 'styles/tools/z-index'
import { spoofLaunchpadCollapsedEvent, spoofLaunchpadExpandedEvent } from 'analytics/snowplow/events'

const SpoofLaunchpad = loadable(() => import('components/App/SpoofLaunchpad/SpoofLaunchpad'))
const SalesSidebar = loadable(() => import('components/App/SalesCopilot/SalesSidebar'))

const LEFT_RAIL_WIDTH = 400
const RIGHT_RAIL_WIDTH = 340

// --copilot-rail-end lets viewport-sized pages such as checkout flights leave room for the docked right rail.
const Workspace = styled.div`
  --rail-top: calc(var(${SPOOF_TOOLBAR_HEIGHT_CSS_VARIABLE}, 0px) + ${props => props.theme.layout.headerFloatingOffset});

  display: flex;
  flex: 1 1 auto;

  &.right-rail-open {
    ${mediaQueryUp.desktop} {
      --copilot-rail-end: ${rem(RIGHT_RAIL_WIDTH)};
    }
  }
`

// A flex column so fixed-height pages such as the search map can still grow to fill the viewport.
const Main = styled.div`
  display: flex;
  flex-direction: column;
  flex: 1 1 auto;
  min-width: 0;
`

// Docked beside the page on desktop. Below that it slides over the page, as there is no room to dock it.
const LeftRail = styled.aside`
  position: fixed;
  top: var(--rail-top);
  bottom: 0;
  inset-inline-start: 0;
  z-index: ${zIndex.floatingPanel};
  width: min(${rem(LEFT_RAIL_WIDTH)}, calc(100vw - ${rem(48)}));
  background-color: ${props => props.theme.palette.neutral.surface.lightBackground};
  border-inline-end: 1px solid ${props => props.theme.palette.neutral.surface.lightForeground};
  box-shadow: ${props => props.theme.shadow.bottom.medium};

  &[hidden] {
    display: none;
  }

  ${mediaQueryUp.desktop} {
    position: static;
    flex: 0 0 ${rem(LEFT_RAIL_WIDTH)};
    width: auto;
    box-shadow: none;
  }
`

const CollapsedLeftRail = styled.aside`
  position: fixed;
  top: calc(var(--rail-top) + ${rem(16)});
  inset-inline-start: 0;
  z-index: ${zIndex.floatingPanel};
  padding: ${rem(8)} ${rem(4)};
  background-color: ${props => props.theme.palette.neutral.surface.lightBackground};
  border: 1px solid ${props => props.theme.palette.neutral.surface.lightForeground};
  border-inline-start: none;
  border-start-end-radius: ${props => props.theme.borderRadius.M};
  border-end-end-radius: ${props => props.theme.borderRadius.M};

  ${mediaQueryUp.desktop} {
    position: static;
    flex: 0 0 auto;
    border-radius: 0;
    border-block: none;
    border-inline-end: 1px solid ${props => props.theme.palette.neutral.surface.lightForeground};
  }
`

// The column stretches to the page height so its background runs the full length.
const RightRail = styled(CSSBreakpoint)`
  flex: 0 0 ${rem(RIGHT_RAIL_WIDTH)};
  background-color: ${props => props.theme.palette.neutral.surface.lightBackground};
  border-inline-start: thin solid ${props => props.theme.palette.neutral.surface.lightForeground};
`

const CollapsedRailContent = styled.div`
  position: sticky;
  top: calc(var(--rail-top) + ${rem(16)});
`

// Sits below the sticky spoof bar and header so the top of the rail is never covered.
const RailContent = styled.div`
  position: sticky;
  top: var(--rail-top);
  max-height: calc((var(--vh) * 100) - var(--rail-top));
  overflow-y: auto;
  padding: ${rem(16)};
  scrollbar-width: thin;
`

// Always renders the same wrapper so the page never remounts when a rail toggles.
function SalesCopilotWorkspace({ children }: PropsWithChildren) {
  const intl = useIntl()
  const { pathname } = useLocation()
  const showCopilot = useAppSelector(state => state.system.showSpoofLaunchpad)
  const isDesktop = useScreenSizeUp('desktop')
  // Until the agent chooses, it is open where it docks and closed where it would cover the page.
  const [collapsedChoice, setLaunchpadCollapsed] = useState<boolean>()
  const launchpadCollapsed = collapsedChoice ?? !isDesktop

  const isCheckout = /\/checkout(\/|$)/.test(pathname)
  // The spoof bar's "Store mode" switch is the inverse of this flag.
  const storeModeOn = useAppSelector(state => !state.system.showMarginInSpoofMode)
  const salesSidebarEnabled = useSalesSidebarEnabled()
  const salesSidebar = useSalesSidebar()
  const showRightRail = salesSidebarEnabled && showCopilot && !storeModeOn && salesSidebar.stage !== 'hidden'
  // The launchpad steps aside once the close-the-sale rail takes over, which only docks on desktop.
  const launchpadSuperseded = isCheckout || (showRightRail && isDesktop)
  // Hidden rather than unmounted, so its state survives collapsing or the close-the-sale stage and activity isn't refetched.
  const hideLeftRail = launchpadSuperseded || launchpadCollapsed

  const collapseLaunchpad = useCallback(() => {
    setLaunchpadCollapsed(true)
    spoofLaunchpadCollapsedEvent()
  }, [])
  const expandLaunchpad = useCallback(() => {
    setLaunchpadCollapsed(false)
    spoofLaunchpadExpandedEvent()
  }, [])

  const launchpadLabel = intl.formatMessage({
    id: 'OKPxvF_jbOdz2XBNiFUI',
    defaultMessage: 'Sales Co-Pilot',
    description: 'Accessible label for the Sales Co-Pilot sidebar that helps sales agents while spoofing a customer',
  })
  const closeTheSaleLabel = intl.formatMessage({
    id: 'R7gpOnWGgMoM9cSSScP2',
    defaultMessage: 'Close the sale',
    description: 'Accessible label for the Sales Co-Pilot right sidebar with upsells and payment options',
  })
  const expandLabel = intl.formatMessage({
    id: 'OB4yCLJPmUGpVofZR1Eq',
    defaultMessage: 'Expand the Sales Co-Pilot',
    description: 'Accessible label for the button that expands the collapsed Sales Co-Pilot sidebar',
  })

  return (
    <Workspace className={showRightRail ? 'right-rail-open' : undefined}>
      {showCopilot && (
        <>
          <LeftRail className="no-print" aria-label={launchpadLabel} hidden={hideLeftRail}>
            <RailContent>
              <SpoofLaunchpad onCollapse={collapseLaunchpad} />
            </RailContent>
          </LeftRail>
          {launchpadCollapsed && !launchpadSuperseded && (
            <CollapsedLeftRail className="no-print" aria-label={launchpadLabel}>
              <CollapsedRailContent>
                <IconButton kind="tertiary" size="small" onClick={expandLaunchpad} aria-label={expandLabel} aria-expanded={false}>
                  <LineAngleRightIcon />
                </IconButton>
              </CollapsedRailContent>
            </CollapsedLeftRail>
          )}
        </>
      )}
      <Main>{children}</Main>
      {showRightRail && (
        <RightRail as="aside" min="desktop" dynamic className="no-print" aria-label={closeTheSaleLabel}>
          <RailContent>
            <ErrorBoundary fallback={null}>
              <SalesSidebar sidebar={salesSidebar} />
            </ErrorBoundary>
          </RailContent>
        </RightRail>
      )}
    </Workspace>
  )
}

export default SalesCopilotWorkspace
